import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type AdminRole = 'system_admin' | 'support';

export interface AdminUser {
  id: string;
  email: string;
  role: AdminRole;
  display_name?: string;
}

interface AuthState {
  accessToken: string | null;
  accessExpiresAt: string | null;
  refreshToken: string | null;
  refreshExpiresAt: string | null;
  user: AdminUser | null;
  setSession: (
    s: Pick<AuthState, 'accessToken' | 'accessExpiresAt' | 'refreshToken' | 'refreshExpiresAt' | 'user'>,
  ) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      accessExpiresAt: null,
      refreshToken: null,
      refreshExpiresAt: null,
      user: null,
      setSession: (s) =>
        set({
          accessToken: s.accessToken,
          accessExpiresAt: s.accessExpiresAt,
          refreshToken: s.refreshToken,
          refreshExpiresAt: s.refreshExpiresAt,
          user: s.user,
        }),
      clear: () =>
        set({
          accessToken: null,
          accessExpiresAt: null,
          refreshToken: null,
          refreshExpiresAt: null,
          user: null,
        }),
      isAuthenticated: () => Boolean(get().accessToken && get().user),
    }),
    {
      name: 'syron.admin.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        accessExpiresAt: state.accessExpiresAt,
        refreshToken: state.refreshToken,
        refreshExpiresAt: state.refreshExpiresAt,
        user: state.user,
      }),
    },
  ),
);

// Imperative helpers so non-React code (axios interceptor) can read/clear
// the session without subscribing.
export const authSnapshot = () => useAuthStore.getState();
export const clearAuth = () => useAuthStore.getState().clear();
